import React, { useMemo } from 'react';
import { Control, Controller, UseFormWatch } from 'react-hook-form';
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { RHFMultiSelect, RHFSelect, RHFTextField } from 'src/components/hook-form';
import { IUnifiedFilter } from 'src/contexts/SearchFilterContext';

import { FilterSectionKey } from './SearchFilter';

interface FilterSectionProps {
  section: FilterSectionKey;
  color: string;
  control: Control<IUnifiedFilter, any>;
  watch: UseFormWatch<IUnifiedFilter>;
  codeStore: any;
  pathname: string;
  onSubmit?: () => void;
}

// 키워드 검색 섹션 - SearchFilter 내부용
const KeywordSection: React.FC = () => {
  return (
    <Stack gap={1}>
      <Typography variant="body1">키워드</Typography>
      <RHFTextField
        name="text"
        label=""
        placeholder="검색어를 입력하세요"
        helperText="[검색 항목]: 프로젝트명, 고객사, 계약사 , ESM 코드 , UUID , 사업관리코드 , 사업기안번호"
        size="small"
        fullWidth
      />
    </Stack>
  );
};

export const FilterSection: React.FC<FilterSectionProps> = ({
  section,
  color,
  control,
  watch,
  codeStore,
  pathname,
  onSubmit,
}) => {
  const theme = useTheme();

  const lostCode = 200307;
  const codeData = useMemo<{
    NEW_STATUS: { label: string; value: number | string }[];
    ONGOING: { label: string; value: number | string }[];
    SIGN: { label: string; value: number }[];
    BZ_STEP: { label: string; value: number | string }[];
  }>(
    () => ({
      NEW_STATUS: codeStore.getCodeByName('NEW_STATUS_CD') ?? [],
      ONGOING: codeStore.getCodeByName('ONGOING_CD') ?? [],
      SIGN: codeStore.getCodeByName('SIGN_CD') ?? [],
      BZ_STEP:
        codeStore.getCodeByName('BZ_STEP_CD').filter(
          // "실주" 제외
          (v: { value: number | string; label: string }) => String(v.value) !== String(lostCode),
        ) ?? [],
    }),
    [codeStore],
  );

  const prefix = section;
  const isProjectPage = section === 'common';
  const capital = isProjectPage
    ? pathname.slice(1).charAt(0).toUpperCase() + pathname.slice(2)
    : prefix.charAt(0).toUpperCase() + prefix.slice(1);

  // 키워드
  if (section === 'keyword') {
    return <KeywordSection />;
  } else {
    return (
      <Stack gap={1}>
        {!isProjectPage && <Typography variant="subtitle1">{capital}</Typography>}

        {/* 신규/기저 */}
        <Stack gap={1}>
          <Typography variant="body1">신규/기저</Typography>
          <Controller
            name={
              isProjectPage
                ? 'newStatusCdList'
                : (`${prefix}NewStatusCdList` as keyof IUnifiedFilter)
            }
            control={control}
            render={({ field }) => (
              <ToggleButtonGroup
                {...field}
                value={field.value ?? []}
                onChange={(_, v) => {
                  if (v.length === 0) return;
                  field.onChange(v);
                }}
                aria-label={`${prefix}NewStatusCdList`}
                sx={{
                  button: { width: '100%' },
                  '& .MuiToggleButtonGroup-root': {
                    '& .MuiToggleButton-root': { width: '100%' },
                  },
                  '& .Mui-selected': {
                    backgroundColor: `${color} !important`,
                    color: `${theme.palette.primary.contrastText} !important`,
                  },
                }}
              >
                {codeData.NEW_STATUS.map((v, i) => (
                  <ToggleButton key={'NEW_STATUS_CD-' + i} value={String(v.value)}>
                    {v.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          />
        </Stack>

        {/* 스팟/지속 */}
        <Stack gap={1}>
          <Typography variant="body1">스팟/지속</Typography>
          <Controller
            name={
              isProjectPage ? 'ongoingCdList' : (`${prefix}OngoingCdList` as keyof IUnifiedFilter)
            }
            control={control}
            render={({ field }) => (
              <ToggleButtonGroup
                {...field}
                value={field.value ?? []}
                onChange={(_, v) => {
                  if (v.length === 0) return;
                  field.onChange(v);
                }}
                sx={{
                  button: { width: '100%' },
                  '& .MuiToggleButtonGroup-root': {
                    '& .MuiToggleButton-root': { width: '100%' },
                  },
                  '& .Mui-selected': {
                    backgroundColor: `${color} !important`,
                    color: `${theme.palette.primary.contrastText} !important`,
                  },
                }}
              >
                {codeData.ONGOING.map((v, i) => (
                  <ToggleButton key={`ONGOING_CD-${i}`} value={String(v.value)}>
                    {v.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          />
        </Stack>

        {/* 영업단계 */}
        <Stack gap={1}>
          <Typography variant="body1">영업단계</Typography>
          <Stack direction="row" gap={1} alignItems="center">
            <Controller
              name={isProjectPage ? 'noBzStepCd' : (`no${capital}BzStepCd` as keyof IUnifiedFilter)}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={!!field.value}
                      onChange={(_, c) => field.onChange(c)}
                    />
                  }
                  label="선택안함"
                  sx={{ '& .MuiSvgIcon-root': { fill: color } }}
                />
              )}
            />
            <RHFMultiSelect
              name={isProjectPage ? 'bzStepCdList' : `${prefix}BzStepCdList`}
              label="단계"
              required={
                // 영업단계 선택안함 해제 시 한개이상 필수
                !watch(
                  isProjectPage ? 'noBzStepCd' : (`no${capital}BzStepCd` as keyof IUnifiedFilter),
                )
              }
              disabled={
                !!watch(
                  isProjectPage ? 'noBzStepCd' : (`no${capital}BzStepCd` as keyof IUnifiedFilter),
                )
              }
              // autoFocus={
              //   !watch(
              //     isProjectPage ? 'noBzStepCd' : (`no${capital}BzStepCd` as keyof IUnifiedFilter),
              //   )
              // }
              checkbox
              all
              options={codeData.BZ_STEP}
              size="small"
              sx={{ minWidth: 100, maxWidth: 250 }}
            />
          </Stack>
        </Stack>

        {/* 확률 필터 */}
        <Stack gap={1}>
          <Typography variant="body1">{'수주확률'}</Typography>
          {/* <Typography variant="body1">{section === 'sales' ? '매출확률' : '수주확률'}</Typography> */}
          <Stack gap={1} direction="row">
            <RHFSelect
              name={isProjectPage ? 'orderPrcnt' : `${prefix}Prcnt`}
              label="확률"
              size="small"
              sx={{ width: 90 }}
            >
              <MenuItem value={''}>선택안함</MenuItem>
              {['0', '10', '30', '50', '70', '90', '100'].map((v) => (
                <MenuItem key={v} value={v}>
                  {v}%
                </MenuItem>
              ))}
            </RHFSelect>
            {watch(isProjectPage ? 'orderPrcnt' : (`${prefix}Prcnt` as keyof IUnifiedFilter)) && (
              <Controller
                name={isProjectPage ? 'orderSign' : (`${prefix}Sign` as keyof IUnifiedFilter)}
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    {codeData.SIGN.map((condition, i) => (
                      <FormControlLabel
                        key={`${condition.value}-${i}`}
                        defaultChecked
                        value={Number(condition.value)}
                        label={condition.label}
                        control={
                          <Radio
                            size="small"
                            color={section === 'sales' ? 'secondary' : 'primary'}
                          />
                        }
                      />
                    ))}
                  </RadioGroup>
                )}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
    );
  }
};
